import React, { createContext, useContext, useState, useEffect } from 'react';

const SidebarContext = createContext();

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

export const SidebarProvider = ({ children }) => {
  // Initialize state from localStorage or defaults
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarOpen');
    return saved !== null ? JSON.parse(saved) : true;
  });

  const [expandedMenus, setExpandedMenus] = useState(() => {
    const saved = localStorage.getItem('expandedMenus');
    if (saved) {
      return JSON.parse(saved);
    }
    // Default state for all menus
    return {
      'Product Management': false,
      'Inventory Management': false,
      'Order Management': false,
      'Partner Management': false,
      'Lead Management': false,
      'Franchisee Management': false,
      'Franchisee Journey': false,
      'Store Operations': false,
      'Store Setup & Launch': false,
      'TRC & FQC': false,
      'Services & Warranty': false,
      'Finance': false,
      'CRM': false,
      'Buyback Engine': false,
      'HRMS': false,
      'Task Management': false,
      'Analytics': false,
      'Supply Chain': false,
      'Legal & Compliance': false,
      'RBAC': false
    };
  });

  // Persist sidebarOpen to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebarOpen', JSON.stringify(sidebarOpen));
  }, [sidebarOpen]);

  // Persist expandedMenus to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('expandedMenus', JSON.stringify(expandedMenus));
  }, [expandedMenus]);

  const toggleSidebar = () => {
    setSidebarOpen(prev => !prev);
  };

  const toggleMenu = (menuName) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  };

  const expandMenu = (menuName) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuName]: true
    }));
  };

  const collapseMenu = (menuName) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuName]: false
    }));
  };

  const collapseAllMenus = () => {
    const collapsed = Object.keys(expandedMenus).reduce((acc, key) => {
      acc[key] = false;
      return acc;
    }, {});
    setExpandedMenus(collapsed);
  };

  const value = {
    sidebarOpen,
    setSidebarOpen,
    toggleSidebar,
    expandedMenus,
    setExpandedMenus,
    toggleMenu,
    expandMenu,
    collapseMenu,
    collapseAllMenus
  };

  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
};
